import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from './lib/supabaseClient';
import { threeWayMerge } from './lib/syncMerge';
import { staffAuthHeaders } from './lib/sbAuth';
import { LoginPage } from './components/LoginPage';
import { PropertyManagementTab } from './components/PropertyManagementTab';
import { LangContext, STRINGS } from './config/i18n';
import { TODAY, FINANCIAL_TABS, DEFAULT_PIN } from './config/constants';
import { buildDefaultUnits } from './lib/helpers';
import { CloudSetupModal, BackupModal } from './components/shared/Modals';
import DashboardTab       from './components/tabs/Dashboard';
import MonthlyTab         from './components/tabs/Monthly';
import MasterContractsTab from './components/tabs/Contracts';
import PartnershipsTab    from './components/tabs/Partnerships';
import FinanceTab         from './components/tabs/Finance';
import OpsManagerTab      from './components/tabs/OpsManager';
import InvoicesTab        from './components/tabs/Invoices';
import SignaturePage       from './components/tabs/Signature';

// ── Seed data & local constants ───────────────────────────────────
// ── Seed Data ─────────────────────────────────────────────────────
const SEED_TENANTS = [];


// ── Seed Lease Costs (from actual spreadsheet) ────────────────────
const SEED_LEASES = {};

// ── Cashflow Payment Schedule Seed ────────────────────────────────
const SEED_CF_SCHEDULE = [];



// ── Master Contracts & Ops Manager Seed ─────────────────────────
const SEED_CONTRACTS = [];

const SEED_OPS_CONFIG = {
  managerName: "حامد",
  fixedSalary: 1500,
  tiers: [
    { label:"خسارة",   minNet:-999999999, maxNet:0,     pct:0  },
    { label:"مستوى ١", minNet:0,          maxNet:15000, pct:10 },
    { label:"مستوى ٢", minNet:15000,      maxNet:30000, pct:13 },
    { label:"مستوى ٣", minNet:30000,      maxNet:999999999, pct:15 },
  ],
  monthlyData: {},  // { "2026-04": { netIncome: 0, note: "" } }
};

// PIN is stored as SHA-256 hash in localStorage (findre-pin-hash)
// Never stored or compared in plaintext after first boot
async function hashPin(pin){
  const data = new TextEncoder().encode(pin + "findre-sa-2026");
  const buf  = await window.crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(buf)).map(b=>b.toString(16).padStart(2,"0")).join("");
}

// ── Main Dashboard (shown after login) ────────────────────────────
function Dashboard({ user, role, onLogout }){
  // Tab persistence: read initial tab from URL hash, sync hash on change.
  // Refreshing the page (or sharing the URL) now keeps you on the same tab.
  // PIN-protected tabs (finance/opsmanager) are NOT restored on refresh — user
  // must re-enter the PIN, so we fall back to dashboard for those on initial load.
  const VALID_TABS = ["dashboard","properties","monthly","contracts","partnerships","finance","opsmanager","invoices"];
  function readTabFromHash(){
    if(typeof window === 'undefined') return "dashboard";
    const h = (window.location.hash || '').replace(/^#/, '');
    if(!VALID_TABS.includes(h)) return "dashboard";
    // Never auto-open PIN-protected tabs without PIN entry.
    if(FINANCIAL_TABS.includes(h)) return "dashboard";
    return h;
  }
  const [tab, setTab]               = useState(readTabFromHash);
  // Sync URL hash whenever the active tab changes
  useEffect(() => {
    if(typeof window === 'undefined') return;
    const desired = `#${tab}`;
    if(window.location.hash !== desired){
      window.history.replaceState(null, '', desired);
    }
  }, [tab]);
  const [tenants, setTenants]       = useState(SEED_TENANTS);
  const [units, setUnits]           = useState(() => buildDefaultUnits(SEED_TENANTS));
  const [monthly, setMonthly]       = useState({});
  const [partners, setPartners]     = useState([
    { id:"p1", name:"محمد الجبرين", phone:"", notes:"شريك في وحدة ٣٠٦ الملقا" }
  ]);
  const [ownership, setOwnership]   = useState({
    "306-الملقا": { managementFeePercent:15, splits:[{ type:"me", name:"FindRE", sharePercent:50 },{ partnerId:"p1", sharePercent:50 }] }
  });
  const [leases, setLeases]         = useState(SEED_LEASES);
  const [cashflowSchedule, setCashflowSchedule] = useState(SEED_CF_SCHEDULE);
  const [contracts, setContracts]   = useState(SEED_CONTRACTS);
  const [opsConfig, setOpsConfig]   = useState(SEED_OPS_CONFIG);
  const [invoices, setInvoices]     = useState([]);
  const [overhead, setOverhead]     = useState([]);   // G&A expenses: SaaS, government, legal, etc.
  const [signatures, setSignatures] = useState({});  // { invoiceNo: { signedAt, dataUrl, customerName } }
  const [loading, setLoading]       = useState(true);
  const [lang, setLang]             = useState(()=>localStorage.getItem("findre-lang")||"ar");
  function toggleLang(){ setLang(l=>{ const n=l==="ar"?"en":"ar"; localStorage.setItem("findre-lang",n); return n; }); }

  // ── Backup state ──
  const [showBackup,    setShowBackup]    = useState(false);
  const [importError,   setImportError]   = useState("");
  const [importSuccess, setImportSuccess] = useState(false);
  const importRef = useRef(null);

  // ── PIN Auth ──
  const [pinUnlocked, setPinUnlocked]   = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pendingTab, setPendingTab]     = useState(null);
  const [pinInput, setPinInput]         = useState("");
  const [pinError, setPinError]         = useState(false);
  const [pinChanging, setPinChanging]   = useState(false);
  const [newPin, setNewPin]             = useState("");
  const [confirmPin, setConfirmPin]     = useState("");
  const [currentPinHash, setCurrentPinHash] = useState("");
  const [pinSuccess, setPinSuccess]     = useState(false);
  const [pinAttempts, setPinAttempts]   = useState(0);
  const [pinLocked, setPinLocked]       = useState(false);
  const [pinLockUntil, setPinLockUntil] = useState(0);

  // Load/init PIN hash from localStorage on first mount
  useEffect(()=>{
    const stored = localStorage.getItem("findre-pin-hash");
    const lockData = JSON.parse(localStorage.getItem("findre-pin-lockout")||"{}");
    if(lockData.lockedUntil && Date.now() < lockData.lockedUntil){
      setPinLocked(true);
      setPinLockUntil(lockData.lockedUntil);
      setPinAttempts(lockData.attempts||0);
    }
    if(stored){
      setCurrentPinHash(stored);
    } else {
      // First boot — hash DEFAULT_PIN and store it
      hashPin(DEFAULT_PIN).then(h=>{ localStorage.setItem("findre-pin-hash",h); setCurrentPinHash(h); });
    }
  },[]);

  // Countdown: auto-unlock after lockout expires
  useEffect(()=>{
    if(!pinLocked) return;
    const iv = setInterval(()=>{
      if(Date.now() >= pinLockUntil){
        setPinLocked(false);
        setPinAttempts(0);
        localStorage.removeItem("findre-pin-lockout");
        clearInterval(iv);
      }
    }, 1000);
    return ()=>clearInterval(iv);
  },[pinLocked, pinLockUntil]);

  function handleTabClick(key){
    if(FINANCIAL_TABS.includes(key) && !pinUnlocked){
      setPendingTab(key);
      setPinInput("");
      setPinError(false);
      setShowPinModal(true);
    } else {
      setTab(key);
    }
  }

  async function submitPin(input){
    // Block if locked out
    const lockData = JSON.parse(localStorage.getItem("findre-pin-lockout")||"{}");
    if(lockData.lockedUntil && Date.now() < lockData.lockedUntil){
      setPinLocked(true);
      setPinLockUntil(lockData.lockedUntil);
      setPinInput("");
      return;
    }
    const h = await hashPin(input);
    if(h === currentPinHash){
      localStorage.removeItem("findre-pin-lockout");
      setPinAttempts(0);
      setPinLocked(false);
      setPinUnlocked(true);
      setShowPinModal(false);
      if(pendingTab) setTab(pendingTab);
      setPendingTab(null);
      setPinInput("");
      setPinError(false);
    } else {
      const next = (lockData.attempts||0) + 1;
      const newLock = next >= 5
        ? { attempts:next, lockedUntil: Date.now() + 5*60*1000 }
        : { attempts:next, lockedUntil:null };
      localStorage.setItem("findre-pin-lockout", JSON.stringify(newLock));
      setPinAttempts(next);
      if(next >= 5){ setPinLocked(true); setPinLockUntil(newLock.lockedUntil); }
      setPinError(true);
      setPinInput("");
    }
  }

  function lockFinancials(){
    setPinUnlocked(false);
    if(FINANCIAL_TABS.includes(tab)) setTab("dashboard");
  }

  async function changePin(){
    if(newPin.length < 4){ setPinError(true); return; }
    if(newPin !== confirmPin){ setPinError(true); return; }
    const h = await hashPin(newPin);
    localStorage.setItem("findre-pin-hash", h);
    setCurrentPinHash(h);
    setNewPin(""); setConfirmPin("");
    setPinChanging(false);
    setPinSuccess(true);
    setTimeout(()=>setPinSuccess(false), 2000);
  }

  // ── localStorage with a real quota guard ──
  // Every write used to be wrapped in catch(e){} — when the 5-10MB quota filled,
  // saves failed silently while the UI still showed "synced".
  const [storageFull, setStorageFull] = useState(false);
  function lsSet(key, value){
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch(err) {
      const quota = err && (err.name === "QuotaExceededError" || err.code === 22 || err.code === 1014);
      if(quota){
        setStorageFull(true);
        setSyncStatus("error");   // surfaces in the existing sync indicator
        console.error(`[Storage] QUOTA FULL — "${key}" was not saved locally. Export a backup and remove old photos.`);
      } else {
        console.error(`[Storage] Failed to save "${key}":`, err);
      }
      return false;
    }
  }

  // ── Supabase Sync Config ──
  // Auto-populate from env vars so it survives localStorage.clear()
  const ENV_SB = (process.env.REACT_APP_SUPABASE_URL && process.env.REACT_APP_SUPABASE_ANON_KEY)
    ? { url: process.env.REACT_APP_SUPABASE_URL, anonKey: process.env.REACT_APP_SUPABASE_ANON_KEY }
    : null;
  const [sbConfig, setSbConfig]     = useState(ENV_SB);  // { url, anonKey }
  const [syncStatus, setSyncStatus] = useState("idle"); // idle | syncing | synced | error
  const [showSbSetup, setShowSbSetup] = useState(false);
  const syncTimer = useRef(null);

  // ── Supabase DB helpers ──
  // Previously this stripped base64 imageUrl before pushing "units". Because the
  // cloud then overwrote local on load, the photo was erased from BOTH sides —
  // it destroyed photos rather than merely skipping them. Photos are now resized
  // to 800px JPEG at upload, so they are small enough to store and sync.
  function stripPhotos(key, value){
    return value;
  }

  async function sbUpsert(cfg, key, value){
    if(!cfg) return;
    const safeValue = stripPhotos(key, value);
    const res = await fetch(`${cfg.url}/rest/v1/findre_data`, {
      method:"POST",
      headers: await staffAuthHeaders(cfg, { "Content-Type":"application/json", "Prefer":"resolution=merge-duplicates" }),
      body: JSON.stringify({ key, value: safeValue })
    });
    if(!res.ok){ const err = await res.text(); throw new Error(`Supabase ${res.status}: ${err}`); }
  }

  function delay(ms){ return new Promise(r => setTimeout(r, ms)); }

  async function sbLoadAll(cfg){
    const res = await fetch(`${cfg.url}/rest/v1/findre_data?select=key,value`, {
      headers: await staffAuthHeaders(cfg)
    });
    if(!res.ok) throw new Error("Supabase fetch failed");
    return res.json(); // [{key, value}, ...]
  }

  function signInWithGoogle(){
    if(!sbConfig){ setShowSbSetup(true); return; }
    // Redirect to Supabase Google OAuth
    window.location.href = `${sbConfig.url}/auth/v1/authorize?provider=google&redirect_to=${encodeURIComponent(window.location.href)}`;
  }

  // Load Supabase config on mount (localStorage overrides env vars only if it has a different URL)
  useEffect(()=>{
    async function loadSbConfig(){
      try {
        const r = await {value: localStorage.getItem("findre-sb-config")};
        if(r && r.value){
          const saved = JSON.parse(r.value);
          // Only override env config if localStorage has a custom URL
          if(saved.url && saved.anonKey) setSbConfig(saved);
        }
      } catch(e){}
      // Always ensure env config is applied as fallback
      if(ENV_SB) setSbConfig(c => c || ENV_SB);
    }
    loadSbConfig();
  },[]);// eslint-disable-line

  // ── Persistent Storage ──
  useEffect(() => {
    async function load(){
      // 1. Load from localStorage
      try { const r = await {value: localStorage.getItem("findre-tenants")};    if(r && r.value) setTenants(JSON.parse(r.value)); } catch(e){}
      try { const r = await {value: localStorage.getItem("findre-units")};      if(r && r.value) setUnits(JSON.parse(r.value));   } catch(e){}
      try { const r = await {value: localStorage.getItem("findre-monthly")};    if(r && r.value) setMonthly(JSON.parse(r.value)); } catch(e){}
      try { const r = await {value: localStorage.getItem("findre-partners")};   if(r && r.value) setPartners(JSON.parse(r.value));} catch(e){}
      try { const r = await {value: localStorage.getItem("findre-ownership")};  if(r && r.value) setOwnership(JSON.parse(r.value));} catch(e){}
      try { const r = await {value: localStorage.getItem("findre-leases")};     if(r && r.value) setLeases(JSON.parse(r.value));  } catch(e){}
      try { const r = await {value: localStorage.getItem("findre-cfschedule")}; if(r && r.value) setCashflowSchedule(JSON.parse(r.value)); } catch(e){}
      try { const r = await {value: localStorage.getItem("findre-contracts")};  if(r && r.value) setContracts(JSON.parse(r.value));  } catch(e){}
      try { const r = await {value: localStorage.getItem("findre-opsconfig")};  if(r && r.value) setOpsConfig(JSON.parse(r.value));  } catch(e){}
      try { const r = await {value: localStorage.getItem("findre-invoices")};   if(r && r.value) setInvoices(JSON.parse(r.value));  } catch(e){}
      try { const r = await {value: localStorage.getItem("findre-overhead")};   if(r && r.value) setOverhead(JSON.parse(r.value));  } catch(e){}
      try { const r = await {value: localStorage.getItem("findre-signatures")}; if(r && r.value) setSignatures(JSON.parse(r.value));} catch(e){}

      // 2. If Supabase configured, try to load from cloud (overrides local — cloud is source of truth)
      // Use localStorage config if set, otherwise fall back to env vars (ENV_SB).
      // This ensures every logged-in user loads shared data even on a fresh browser.
      try {
        const sbr = await {value: localStorage.getItem("findre-sb-config")};
        const cfg = (sbr && sbr.value) ? JSON.parse(sbr.value) : ENV_SB;
        if(cfg){
          setSbConfig(cfg);
          setSyncStatus("syncing");
          const rows = await sbLoadAll(cfg);
          const map = {};
          rows.forEach(r=>{ map[r.key]=r.value; });
          // Merge cloud into local instead of overwriting it.
          // The old code let the cloud win outright, which is how unit photos and
          // any edit made while offline were silently erased. The "prefer the longer
          // contracts array" hack is gone — the three-way merge handles it properly.
          const LS_KEYS = {
            tenants:"findre-tenants", units:"findre-units", monthly:"findre-monthly",
            partners:"findre-partners", ownership:"findre-ownership", leases:"findre-leases",
            cashflowSchedule:"findre-cfschedule", contracts:"findre-contracts",
            opsConfig:"findre-opsconfig", invoices:"findre-invoices",
            overhead:"findre-overhead", signatures:"findre-signatures",
          };
          const LOAD_SETTERS = {
            tenants:setTenants, units:setUnits, monthly:setMonthly, partners:setPartners,
            ownership:setOwnership, leases:setLeases, cashflowSchedule:setCashflowSchedule,
            contracts:setContracts, opsConfig:setOpsConfig, invoices:setInvoices,
            overhead:setOverhead, signatures:setSignatures,
          };

          // What the cloud held at our last sync, persisted so it survives a reload.
          let prevBase = {};
          try { prevBase = JSON.parse(localStorage.getItem("findre-sync-base") || "{}"); } catch(e){}

          for(const [k, lsKey] of Object.entries(LS_KEYS)){
            let localVal = null;
            try { const raw = localStorage.getItem(lsKey); localVal = raw ? JSON.parse(raw) : null; } catch(e){}
            const remoteVal = (map[k] === undefined) ? null : map[k];
            const mergedVal = threeWayMerge(prevBase[k], localVal, remoteVal);
            if(mergedVal === null || mergedVal === undefined) continue;
            LOAD_SETTERS[k](mergedVal);
            lsSet(lsKey, mergedVal);
            // If our merge produced something the cloud does not have yet, make sure
            // it gets pushed — otherwise a rescued photo would never leave this device.
            if(JSON.stringify(mergedVal) !== JSON.stringify(remoteVal)) dirtyRef.current.add(k);
          }

          // Base = what the cloud actually holds right now.
          baseRef.current = JSON.parse(JSON.stringify(map));
          lsSet("findre-sync-base", map);
          if(dirtyRef.current.size && cfg) triggerSync(null, null);
          setSyncStatus("synced");
        }
      } catch(e){ setSyncStatus("error"); }

      setLoading(false);
    }
    load();
  }, []);

  // Always-current refs
  const latestState = useRef({});
  // What the cloud held at our last successful sync, per key.
  // Used as the merge base so a stale tab can't overwrite the other device.
  const baseRef  = useRef({});
  // Keys this browser has actually edited since the last flush.
  const dirtyRef = useRef(new Set());
  const sbConfigRef = useRef(null);
  useEffect(()=>{ sbConfigRef.current = sbConfig; }, [sbConfig]);
  useEffect(()=>{
    latestState.current = {tenants,units,monthly,partners,ownership,leases,cashflowSchedule,contracts,opsConfig,invoices,overhead,signatures};
  });

  // Apply a merged value back into React state.
  const SETTERS = {
    tenants:setTenants, units:setUnits, monthly:setMonthly, partners:setPartners,
    ownership:setOwnership, leases:setLeases, cashflowSchedule:setCashflowSchedule,
    contracts:setContracts, opsConfig:setOpsConfig, invoices:setInvoices,
    overhead:setOverhead, signatures:setSignatures,
  };

  // Read one key straight from the cloud.
  async function sbGetKey(cfg, key){
    const res = await fetch(
      `${cfg.url}/rest/v1/findre_data?select=value&key=eq.${encodeURIComponent(key)}&limit=1`,
      { headers: await staffAuthHeaders(cfg) }
    );
    if(!res.ok) throw new Error(`Supabase read ${res.status}`);
    const rows = await res.json();
    return rows[0]?.value ?? null;
  }

  // Write ONE key, merging against whatever the other device has done since
  // our last sync. Never uploads keys this browser did not touch.
  async function pushKeyMerged(cfg, key, localValue){
    if(!cfg) return;
    let merged = localValue;
    try {
      const remote = await sbGetKey(cfg, key);
      merged = threeWayMerge(baseRef.current[key], localValue, remote);
    } catch(e){
      // Cloud unreadable — fall back to writing what we have rather than losing it.
      console.warn(`[sync] merge read failed for "${key}", writing local:`, e.message);
    }
    await sbUpsert(cfg, key, merged);
    baseRef.current[key] = JSON.parse(JSON.stringify(merged));
    lsSet("findre-sync-base", baseRef.current);
    // Reflect the merge locally so both devices converge on screen.
    const setter = SETTERS[key];
    if(setter && merged !== localValue) setter(merged);
    return merged;
  }

  async function flushSync(cfg){
    if(!cfg) return;
    const s = latestState.current;
    const keys = Array.from(dirtyRef.current);
    dirtyRef.current = new Set();
    for(const k of keys){
      await pushKeyMerged(cfg, k, s[k]);
      await delay(300);
    }
  }

  // Save when tab is hidden (covers refresh, close, tab switch)
  useEffect(()=>{
    async function handleVisibility(){
      if(document.visibilityState==="hidden" && sbConfigRef.current && dirtyRef.current.size){
        clearTimeout(syncTimer.current);
        syncTimer.current = null;
        try{ await flushSync(sbConfigRef.current); }catch(e){}
      }
    }
    document.addEventListener("visibilitychange", handleVisibility);
    return ()=>document.removeEventListener("visibilitychange", handleVisibility);
  },[]);

  // Debounced sync — 3s after last edit
  function triggerSync(key, data){
    if(!sbConfig) return;
    if(key) dirtyRef.current.add(key);
    setSyncStatus("syncing");
    clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(async()=>{
      try{
        await flushSync(sbConfig);
        setSyncStatus("synced");
      }catch(e){ console.error("Sync error:",e); setSyncStatus("error"); }
    }, 3000);
  }

  const saveTenants = useCallback(async (data) => {
    lsSet("findre-tenants", data);
    triggerSync("tenants", data);
  }, [sbConfig]);
  const saveUnits = useCallback(async (data) => {
    lsSet("findre-units", data);
    triggerSync("units", data);
  }, [sbConfig]);
  const saveMonthly = useCallback(async (data) => {
    lsSet("findre-monthly", data);
    triggerSync("monthly", data);
  }, [sbConfig]);
  const savePartners = useCallback(async (data) => {
    lsSet("findre-partners", data);
    triggerSync("partners", data);
  }, [sbConfig]);
  const saveOwnership = useCallback(async (data) => {
    lsSet("findre-ownership", data);
    triggerSync("ownership", data);
  }, [sbConfig]);
  const saveLeases = useCallback(async (data) => {
    lsSet("findre-leases", data);
    triggerSync("leases", data);
  }, [sbConfig]);
  const saveCashflowSchedule = useCallback(async (data) => {
    lsSet("findre-cfschedule", data);
    triggerSync("cashflowSchedule", data);
  }, [sbConfig]);
  const saveContracts = useCallback(async (data) => {
    lsSet("findre-contracts", data);
    // Write contracts to Supabase immediately (no debounce) to prevent loss on fast reload
    if(sbConfig){ pushKeyMerged(sbConfig, "contracts", data).catch(e=>console.warn("contracts immediate sync failed:", e)); }
    triggerSync("contracts", data);
  }, [sbConfig]);
  const saveOpsConfig = useCallback(async (data) => {
    lsSet("findre-opsconfig", data);
    triggerSync("opsConfig", data);
  }, [sbConfig]);
  const saveInvoices = useCallback(async (data) => {
    lsSet("findre-invoices", data);
    // Write invoices to Supabase IMMEDIATELY (no debounce) — on mobile the user
    // typically taps the WhatsApp link right after submit, which puts the tab in
    // background. The 3s debounced sync never fires and the invoice is lost on
    // next page load (Supabase data overwrites local). Same pattern as contracts.
    if(sbConfig){ pushKeyMerged(sbConfig, "invoices", data).catch(e=>console.warn("invoices immediate sync failed:", e)); }
    triggerSync("invoices", data);
  }, [sbConfig]);
  const saveSignatures = useCallback(async (data) => {
    lsSet("findre-signatures", data);
    // No triggerSync — customer is the source of truth and writes directly via REST
  }, [sbConfig]);
  const saveOverhead = useCallback(async (data) => {
    lsSet("findre-overhead", data);
    triggerSync("overhead", data);
  }, [sbConfig]);

  // Pull signatures from Supabase (called on archive view + manual refresh)
  async function refreshSignatures(){
    if(!sbConfig) return;
    try {
      const res = await fetch(`${sbConfig.url}/rest/v1/findre_data?select=key,value&key=eq.signatures`, {
        headers: await staffAuthHeaders(sbConfig)
      });
      if(!res.ok) return;
      const rows = await res.json();
      if(rows[0]?.value){
        setSignatures(rows[0].value);
        saveSignatures(rows[0].value);
      }
    } catch(e){ console.warn("refresh signatures failed:", e); }
  }

  async function saveSbConfig(cfg){
    try { await localStorage.setItem("findre-sb-config", JSON.stringify(cfg)); } catch(e){}
    setSbConfig(cfg);
    setShowSbSetup(false);
    // Push all current data to Supabase
    setSyncStatus("syncing");
    try {
      const entries=[["tenants",tenants],["units",units],["monthly",monthly],["partners",partners],["ownership",ownership],["leases",leases],["cashflowSchedule",cashflowSchedule],["contracts",contracts],["opsConfig",opsConfig],["invoices",invoices],["overhead",overhead],["signatures",signatures]];
      for(const [key,val] of entries){ await sbUpsert(cfg,key,val); await delay(400); }
      setSyncStatus("synced");
    } catch(e){ console.error("Sync error:",e); setSyncStatus("error"); }
  }

  async function manualSync(){
    if(!sbConfig) return;
    setSyncStatus("syncing");
    try {
      const entries=[["tenants",tenants],["units",units],["monthly",monthly],["partners",partners],["ownership",ownership],["leases",leases],["cashflowSchedule",cashflowSchedule],["contracts",contracts],["opsConfig",opsConfig],["invoices",invoices],["overhead",overhead],["signatures",signatures]];
      for(const [key,val] of entries){ await pushKeyMerged(sbConfig,key,val); await delay(400); }
      setSyncStatus("synced");
    } catch(e){ console.error("Sync error:",e); setSyncStatus("error"); }
  }

  function updateTenants(fn){ setTenants(prev=>{ const next=fn(prev); saveTenants(next); return next; }); }
  function updateUnits(fn){   setUnits(prev=>{ const next=fn(prev); saveUnits(next); return next; }); }
  function handleAddUnit(newUnit){
    const unitKey = newUnit.unit || newUnit.unitNumber;
    updateUnits(prev=>[...prev, {...newUnit, unit:unitKey, available:true}]);
    updateTenants(prev=>{
      if(prev.find(t=>t.unit===unitKey&&t.district===newUnit.district)) return prev;
      return [...prev,{id:Date.now(),unit:unitKey,district:newUnit.district,name:'',phone:'',contractStart:'',contractEnd:'',rent:0,available:true,payments:{}}];
    });
  }
  function handleDeleteUnit(id){
    const unit = units.find(u=>u.id===id);
    updateUnits(prev=>prev.filter(u=>u.id!==id));
    if(unit){
      const unitKey = unit.unit||unit.unitNumber;
      updateTenants(prev=>prev.filter(t=>!(t.unit===unitKey&&t.district===unit.district)));
    }
  }
  function updateMonthly(fn){ setMonthly(prev=>{ const next=fn(prev); saveMonthly(next); return next; }); }
  function updatePartners(fn){ setPartners(prev=>{ const next=fn(prev); savePartners(next); return next; }); }
  function updateOwnership(fn){ setOwnership(prev=>{ const next=fn(prev); saveOwnership(next); return next; }); }
  function updateLeases(fn){ setLeases(prev=>{ const next=fn(prev); saveLeases(next); return next; }); }
  function updateCashflowSchedule(fn){ setCashflowSchedule(prev=>{ const next=fn(prev); saveCashflowSchedule(next); return next; }); }
  function updateContracts(fn){ setContracts(prev=>{ const next=fn(prev); saveContracts(next); return next; }); }
  function updateOpsConfig(fn){ setOpsConfig(prev=>{ const next=fn(prev); saveOpsConfig(next); return next; }); }
  function updateInvoices(fn){ setInvoices(prev=>{ const next=fn(prev); saveInvoices(next); return next; }); }
  function updateOverhead(fn){ setOverhead(prev=>{ const next=fn(prev); saveOverhead(next); return next; }); }
  function addInvoice(record){
    // Belt-and-suspenders: clear any orphan signature for this invoice number
    // before adding. Guards against a stale signature from a previously-deleted
    // invoice incorrectly marking the new one as already-signed.
    if(record?.invoiceNo && signatures[record.invoiceNo]){
      clearSignature(record.invoiceNo);
    }
    updateInvoices(prev=>[...prev, record]);
    // Use the master contract ID as the monthly key prefix (matches Contracts tab + Monthly tab).
    // Fall back to unit+district string only if no matching contract is found.
    const matchContract = contracts.find(c =>
      (c.unitNumber === record.unit || c.unitName === record.unit) &&
      ((c.district||'') === (record.district||''))
    );
    const monthlyUnitId = matchContract ? matchContract.id : (record.unit + '-' + record.district);
    updateUnits(prev => prev.map(u =>
      (u.unit===record.unit||u.unitNumber===record.unit) && (u.district||'')===(record.district||'')
        ? { ...u, available:false, name:record.fullName||record.customerName||u.name,
            phone:record.customerPhone||record.phone||u.phone,
            contractStart:record.contractStart, contractEnd:record.contractEnd, rent:Number(record.rent)||0 }
        : u
    ));
    updateTenants(prev => {
      const ex = prev.find(t => t.unit===record.unit && t.district===record.district);
      const rec = { id:ex?ex.id:Date.now(), unit:record.unit, district:record.district,
        name:record.fullName||record.customerName||'', phone:record.customerPhone||record.phone||'',
        contractStart:record.contractStart, contractEnd:record.contractEnd,
        rent:Number(record.rent)||0, available:false, payments:ex?ex.payments:{} };
      return ex ? prev.map(t => t.unit===record.unit&&t.district===record.district ? rec : t) : [...prev, rec];
    });
    if(record.contractStart && record.contractEnd){
      updateMonthly(prev => {
        const next = Object.assign({}, prev);
        const d = new Date(record.contractStart.slice(0,7)+'-01');
        const endD = new Date(record.contractEnd.slice(0,7)+'-01');
        while(d <= endD){
          const mk = d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0');
          const flatKey = `${monthlyUnitId}-${mk}`;
          const existing = next[flatKey] || {};
          // Preserve any manually-entered rentCollected and tenants array — never overwrite
          // the user's hand-logged revenue when an invoice gets added. Only backfill empty cells.
          const existingRent = Number(existing.rentCollected)||0;
          const existingName = (existing.tenantName||'').trim();
          const newName = record.fullName||record.customerName||'';
          next[flatKey] = Object.assign({}, existing, {
            status: 'مشغول',
            tenantName: existingName || newName,
            rentCollected: existingRent, // keep what the user already entered; invoices don't overwrite
          });
          d.setMonth(d.getMonth()+1);
        }
        return next;
      });
    }
  }
  function updateInvoice(id, patch){ updateInvoices(prev=>prev.map(i=>i.id===id?{...i,...patch}:i)); }
  function deleteInvoice(id){
    const inv = invoices.find(i=>i.id===id);
    updateInvoices(prev=>prev.filter(i=>i.id!==id));
    // Clear any signature attached to this invoice number — otherwise a future
    // invoice that gets the same number (e.g. after deleting the latest one)
    // would show as already-signed because the orphan signature persists.
    if(inv?.invoiceNo){ clearSignature(inv.invoiceNo); }
    if(!inv) return;

    // Build the set of monthly keys this invoice could have written to. We need
    // to cover BOTH the contract period (set by addInvoice with rentCollected=0)
    // AND the paidAt month (where markInvoicePaid recorded the actual revenue —
    // could land outside the contract period if payment was late/early).
    const matchContract = contracts.find(c =>
      c.unitNumber === inv.unit || c.unitName === inv.unit
    );
    // Try multiple unit-id formats — the contract.id may have changed since the
    // invoice was originally written, so we'll sweep both modern + legacy keys.
    const possibleUnitIds = Array.from(new Set([
      matchContract && matchContract.id,
      `${inv.unit}-${inv.district||''}`,
      `${inv.unit}-${inv.district}`,
    ].filter(Boolean)));

    const months = new Set();
    if(inv.contractStart && inv.contractEnd){
      const d = new Date(inv.contractStart.slice(0,7)+'-01');
      const endD = new Date(inv.contractEnd.slice(0,7)+'-01');
      while(d <= endD){
        months.add(d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0'));
        d.setMonth(d.getMonth()+1);
      }
    }
    if(inv.paidAt) months.add(String(inv.paidAt).slice(0,7));

    // Clean monthly entries that belong to THIS tenant (strict match by tenantName).
    // CRITICAL: only clear cells whose tenantName EXACTLY matches the invoice's customer.
    // Never clear an empty-tenantName cell — those are manual entries the user typed
    // without a name, and wiping them loses real logged revenue.
    updateMonthly(prev => {
      const next = Object.assign({}, prev);
      const myTenant = (inv.customerName || inv.fullName || '').trim();
      if (!myTenant) return next; // can't safely identify ownership → leave everything alone
      for(const unitId of possibleUnitIds){
        for(const mk of months){
          const flatKey = `${unitId}-${mk}`;
          const entry   = next[flatKey];
          if(!entry) continue;
          const entryTenant = (entry.tenantName || '').trim();
          if (entryTenant === myTenant){
            // Clear the tenant/rent fields but keep electricity, maintenance, notes, snapshot.
            next[flatKey] = { ...entry, status:'شاغرة', tenantName:'', rentCollected:0, tenants:[] };
          }
        }
      }
      return next;
    });

    // Units + tenants cleanup — only if no remaining invoices for this unit.
    const others = invoices.filter(i => i.id !== id && i.unit === inv.unit && i.district === inv.district);
    if(others.length === 0){
      updateUnits(prev => prev.map(u =>
        (u.unit===inv.unit||u.unitNumber===inv.unit) && (u.district||'')===(inv.district||'')
          ? { ...u, available:true, name:'', phone:'', contractStart:'', contractEnd:'', rent:0 }
          : u
      ));
      updateTenants(prev => prev.map(t =>
        t.unit===inv.unit && t.district===inv.district
          ? { ...t, available:true, name:'', phone:'', contractStart:'', contractEnd:'', rent:0 }
          : t
      ));
    }
  }
  async function clearSignature(invoiceNo){
    setSignatures(prev=>{
      const next = {...prev};
      delete next[invoiceNo];
      saveSignatures(next);
      // Also remove from Supabase if connected
      if(sbConfig){
        sbUpsert(sbConfig, "signatures", next).catch(e=>console.warn("clear sig failed:", e));
      }
      return next;
    });
  }

  // ── Export / Import ──
  function exportData(){
    // v1.1 — contracts, invoices, opsConfig, overhead and signatures were missing
    // from v1.0 exports, which made the "backup" useless for restoring the records
    // that matter most.
    const payload = {
      version: "1.1",
      exportedAt: new Date().toISOString(),
      data: {
        tenants, units, monthly, partners, ownership, leases, cashflowSchedule,
        contracts, opsConfig, invoices, overhead, signatures
      }
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], {type:"application/json"});
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url;
    a.download = `findre-backup-${new Date().toISOString().slice(0,10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function importData(file){
    if(!file) return;
    const reader = new FileReader();
    reader.onload = (e)=>{
      try {
        const payload = JSON.parse(e.target.result);
        if(!payload.data) throw new Error("ملف غير صالح");
        const d = payload.data;
        if(d.tenants)          { setTenants(d.tenants);          saveTenants(d.tenants); }
        if(d.units)            { setUnits(d.units);              saveUnits(d.units); }
        if(d.monthly)          { setMonthly(d.monthly);          saveMonthly(d.monthly); }
        if(d.partners)         { setPartners(d.partners);        savePartners(d.partners); }
        if(d.ownership)        { setOwnership(d.ownership);      saveOwnership(d.ownership); }
        if(d.leases)           { setLeases(d.leases);            saveLeases(d.leases); }
        if(d.cashflowSchedule) { setCashflowSchedule(d.cashflowSchedule); saveCashflowSchedule(d.cashflowSchedule); }
        // v1.1 keys — absent from older backup files, so each is optional.
        if(d.contracts)        { setContracts(d.contracts);        saveContracts(d.contracts); }
        if(d.opsConfig)        { setOpsConfig(d.opsConfig);        saveOpsConfig(d.opsConfig); }
        if(d.invoices)         { setInvoices(d.invoices);          saveInvoices(d.invoices); }
        if(d.overhead)         { setOverhead(d.overhead);          saveOverhead(d.overhead); }
        if(d.signatures)       { setSignatures(d.signatures);      saveSignatures(d.signatures); }
        setImportSuccess(true);
        setImportError("");
        setTimeout(()=>{ setShowBackup(false); setImportSuccess(false); }, 1800);
      } catch(err){
        setImportError("خطأ في قراءة الملف — تأكد أنه ملف FindRE صالح");
      }
    };
    reader.readAsText(file);
  }

  const _S = STRINGS[lang];
  // Build tabs from i18n strings and insert Properties tab after Dashboard
  const baseTabs = Object.entries(_S.tabs).map(([key,label])=>({key,label}));
  const propertiesLabel = "🌐 Online Properties";
  const TABS = [baseTabs[0], { key:"properties", label:propertiesLabel }, ...baseTabs.slice(1)];
  if(loading) return(
    <LangContext.Provider value={lang}>
    <div dir={lang==="ar"?"rtl":"ltr"} style={{minHeight:"100vh",background:"#0d0f1a",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:lang==="ar"?"'Tajawal',sans-serif":"'Inter','Segoe UI',sans-serif",color:"#c8a03c",fontSize:18}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@400;700;800&display=swap');`}</style>
      {_S.loading}
    </div>
    </LangContext.Provider>
  );

  return(
    <LangContext.Provider value={lang}>
    <div dir={lang==="ar"?"rtl":"ltr"} style={{minHeight:"100vh",background:"#0d0f1a",color:"#e2e8f0",fontFamily:lang==="ar"?"'Tajawal','Arial',sans-serif":"'Inter','Segoe UI',sans-serif"}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@300;400;500;700;800&display=swap');
        *{box-sizing:border-box}
        ::-webkit-scrollbar{width:5px;height:5px}
        ::-webkit-scrollbar-thumb{background:rgba(200,160,60,0.25);border-radius:3px}
        input::placeholder,textarea::placeholder{color:#334155}
        .trow:hover td{background:rgba(255,255,255,0.018)!important}
        .act-btn{opacity:0;transition:opacity .18s}
        .trow:hover .act-btn{opacity:1}
        .unit-card:hover{border-color:rgba(200,160,60,0.4)!important;transform:translateY(-2px)}
        .unit-card{transition:border-color .2s,transform .2s}
        .cell-hover:hover{filter:brightness(1.15);cursor:pointer}
        .tab-scroll::-webkit-scrollbar{height:0}
      `}</style>

      {/* ── Header ── */}
      <div style={{borderBottom:"1px solid rgba(255,255,255,0.06)",background:"rgba(0,0,0,0.35)",backdropFilter:"blur(10px)",position:"sticky",top:0,zIndex:100}}>

        {/* Top bar: logo + actions */}
        <div style={{padding:"10px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
          {/* Logo */}
          <div style={{display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
            <div style={{width:34,height:34,borderRadius:9,background:"linear-gradient(135deg,#c8a03c,#7a5010)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,fontWeight:800,color:"#0d0f1a"}}>F</div>
            <div>
              <div style={{fontSize:18,fontWeight:800,color:"#c8a03c",lineHeight:1}}>FindRE</div>
              <div style={{fontSize:9,color:"#475569",letterSpacing:1.5}}>{_S.subtitle}</div>
            </div>
          </div>

          {/* User info + Logout — right side */}
          <div style={{display:"flex",alignItems:"center",gap:12,flexShrink:0}}>
            {role && (
              <span style={{fontSize:11,color:"#94a3b8",background:"rgba(255,255,255,0.04)",padding:"4px 10px",borderRadius:6,border:"1px solid rgba(255,255,255,0.08)"}}>
                {role === 'admin' ? '👑 Admin' : '👤 Operations Manager'}
              </span>
            )}
            {user && (
              <span style={{fontSize:11,color:"#64748b"}}>{user.email}</span>
            )}
            <button onClick={onLogout} style={{padding:"5px 12px",borderRadius:7,background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.2)",color:"#ef4444",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif",whiteSpace:"nowrap"}}>
              Logout
            </button>
          </div>

          {/* Action buttons — compact on mobile */}
          <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"nowrap",overflowX:"auto"}}>
            <button onClick={toggleLang} style={{padding:"5px 10px",borderRadius:7,background:"rgba(200,160,60,0.1)",border:"1px solid rgba(200,160,60,0.25)",color:"#c8a03c",fontSize:11,fontWeight:800,cursor:"pointer",whiteSpace:"nowrap",letterSpacing:0.5}}>
              {lang==="ar"?"EN":"AR"}
            </button>
            {pinUnlocked&&(
              <button onClick={lockFinancials} style={{padding:"5px 10px",borderRadius:7,background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.2)",color:"#ef4444",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif",whiteSpace:"nowrap"}}>🔒</button>
            )}
            {sbConfig?(
              <button onClick={manualSync} style={{padding:"5px 10px",borderRadius:7,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif",border:"1px solid",whiteSpace:"nowrap",
                background:syncStatus==="synced"?"rgba(34,197,94,0.08)":syncStatus==="syncing"?"rgba(245,158,11,0.08)":"rgba(239,68,68,0.08)",
                color:syncStatus==="synced"?"#22c55e":syncStatus==="syncing"?"#f59e0b":"#ef4444",
                borderColor:syncStatus==="synced"?"rgba(34,197,94,0.25)":syncStatus==="syncing"?"rgba(245,158,11,0.25)":"rgba(239,68,68,0.25)"}}>
                {syncStatus==="synced"?"☁️":syncStatus==="syncing"?"⏳":"⚠️"}
              </button>
            ):(
              <button onClick={()=>setShowSbSetup(true)} style={{padding:"5px 10px",borderRadius:7,background:"rgba(167,139,250,0.08)",border:"1px solid rgba(167,139,250,0.2)",color:"#a78bfa",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif",whiteSpace:"nowrap"}}>☁️</button>
            )}
            <button onClick={()=>setShowBackup(true)} style={{padding:"5px 10px",borderRadius:7,background:"rgba(56,189,248,0.1)",border:"1px solid rgba(56,189,248,0.22)",color:"#38bdf8",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif",whiteSpace:"nowrap"}}>💾</button>
            <div style={{fontSize:11,color:"#475569",background:"rgba(255,255,255,0.04)",padding:"5px 10px",borderRadius:7,border:"1px solid rgba(255,255,255,0.06)",whiteSpace:"nowrap",display:"none"}} className="desktop-date">
              {TODAY.toISOString().slice(0,10)}
            </div>
          </div>
        </div>

        {/* Tab bar — horizontal scroll on mobile */}
        <div className="tab-scroll" style={{display:"flex",gap:4,overflowX:"auto",padding:"0 12px 10px",scrollbarWidth:"none"}}>
          {TABS.filter(t => {
            // Hide Finance tabs for operations_manager
            if (role === 'operations_manager' && FINANCIAL_TABS.includes(t.key)) {
              return false;
            }
            return true;
          }).map(t=>{
            const isFinancial = FINANCIAL_TABS.includes(t.key);
            const locked = isFinancial && !pinUnlocked;
            const isActive = tab===t.key;
            return(
              <button key={t.key} onClick={()=>handleTabClick(t.key)}
                style={{padding:"7px 12px",borderRadius:9,border:"1px solid",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"'Tajawal',sans-serif",
                  whiteSpace:"nowrap",flexShrink:0,
                  background:isActive?"rgba(200,160,60,0.18)":"rgba(255,255,255,0.03)",
                  color:isActive?"#c8a03c":locked?"#475569":"#64748b",
                  borderColor:isActive?"rgba(200,160,60,0.4)":"rgba(255,255,255,0.06)"}}>
                {t.label}{locked?" 🔒":""}
              </button>
            );
          })}
        </div>
      </div>

      {tab==="dashboard"      && <DashboardTab    tenants={tenants} setTenants={updateTenants} units={units} contracts={contracts} invoices={invoices} monthly={monthly} setMonthly={updateMonthly}/>}
      {tab==="properties"     && <PropertyManagementTab user={user} role={role}/>}
      {tab==="monthly"        && <MonthlyTab      contracts={contracts} monthly={monthly} setMonthly={updateMonthly} tenants={tenants} ownership={ownership} overhead={overhead} setOverhead={updateOverhead}/>}
      {tab==="contracts"      && <MasterContractsTab contracts={contracts} setContracts={updateContracts} units={units} setUnits={updateUnits} monthly={monthly} setMonthly={updateMonthly} sbConfig={sbConfig}/>}
      {tab==="partnerships"   && <PartnershipsTab partners={partners} setPartners={updatePartners} ownership={ownership} setOwnership={updateOwnership} units={units} monthly={monthly}/>}
      {tab==="finance"        && <FinanceTab      units={units} leases={leases} setLeases={updateLeases} monthly={monthly} tenants={tenants} schedule={cashflowSchedule} setSchedule={updateCashflowSchedule} contracts={contracts} setContracts={updateContracts} invoices={invoices} overhead={overhead} setOverhead={updateOverhead}/>}
      {tab==="opsmanager"     && <OpsManagerTab   monthly={monthly} units={units} opsConfig={opsConfig} setOpsConfig={updateOpsConfig} leases={leases}/>}
      {tab==="invoices"       && <InvoicesTab tenants={tenants} units={units} contracts={contracts} invoices={invoices} addInvoice={addInvoice} updateInvoice={updateInvoice} deleteInvoice={deleteInvoice} clearSignature={clearSignature} sbConfig={sbConfig} signatures={signatures} refreshSignatures={refreshSignatures} setMonthly={updateMonthly}/>}

      {/* ── Backup Modal ── */}
      {showBackup && (
        <BackupModal
          onClose={()=>{ setShowBackup(false); setImportError(""); setImportSuccess(false); }}
          onExport={exportData}
          onImport={importData}
          importError={importError}
          importSuccess={importSuccess}
          onGoogleSignIn={signInWithGoogle}
          sbConfigured={!!sbConfig}
        />
      )}

      {/* ── Supabase Cloud Setup Modal ── */}
      {showSbSetup && (
        <CloudSetupModal
          onClose={()=>setShowSbSetup(false)}
          onSave={saveSbConfig}
          existing={sbConfig}
          onDisconnect={()=>{ setSbConfig(null); localStorage.removeItem("findre-sb-config"); setShowSbSetup(false); setSyncStatus("idle"); }}
        />
      )}

      {/* ── PIN Modal ── */}
      {showPinModal && (
        <div style={{position:"fixed",inset:0,zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,0.85)",backdropFilter:"blur(8px)"}}>
          <div dir="rtl" style={{background:"#181b27",border:"1px solid rgba(200,160,60,0.3)",borderRadius:20,padding:32,width:"min(380px,92vw)",textAlign:"center",fontFamily:"'Tajawal',sans-serif"}}>
            <div style={{fontSize:40,marginBottom:12}}>🔐</div>
            <div style={{fontSize:18,fontWeight:800,color:"#c8a03c",marginBottom:6}}>
              {pinChanging ? _S.pin.changeTitle : _S.pin.title}
            </div>
            <div style={{fontSize:13,color:"#475569",marginBottom:24}}>
              {pinChanging ? _S.pin.changeSubtitle : _S.pin.subtitle}
            </div>

            {!pinChanging ? (
              <>
                {/* PIN dots display */}
                <div style={{display:"flex",gap:12,justifyContent:"center",marginBottom:20}}>
                  {[0,1,2,3].map(i=>(
                    <div key={i} style={{width:14,height:14,borderRadius:"50%",background:pinInput.length>i?"#c8a03c":"rgba(255,255,255,0.1)",border:"2px solid",borderColor:pinInput.length>i?"#c8a03c":"rgba(255,255,255,0.15)",transition:"background .15s"}}/>
                  ))}
                </div>

                {/* Lockout message */}
                {pinLocked && (
                  <div style={{fontSize:13,color:"#ef4444",marginBottom:16,fontWeight:700,background:"rgba(239,68,68,0.08)",padding:"10px 14px",borderRadius:10,border:"1px solid rgba(239,68,68,0.2)"}}>
                    🔒 {_S.pin.locked
                      ? _S.pin.locked.replace("{m}", Math.max(1, Math.ceil((pinLockUntil - Date.now()) / 60000)))
                      : `محظور — حاول بعد ${Math.max(1, Math.ceil((pinLockUntil - Date.now()) / 60000))} دقيقة`}
                  </div>
                )}

                {/* Number pad */}
                <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:16}}>
                  {[1,2,3,4,5,6,7,8,9,"","0","⌫"].map((n,i)=>(
                    <button key={i} onClick={()=>{
                      if(pinLocked) return;
                      if(n==="⌫"){ setPinInput(p=>p.slice(0,-1)); setPinError(false); }
                      else if(n!==""){ const next=pinInput+n; setPinInput(next); setPinError(false); if(next.length===4) setTimeout(()=>submitPin(next),150); }
                    }} disabled={n===""||pinLocked} style={{padding:"14px",borderRadius:12,border:"1px solid",cursor:n===""||pinLocked?"default":"pointer",fontSize:18,fontWeight:700,fontFamily:"'Tajawal',sans-serif",
                      background:n===""||pinLocked?"transparent":pinError?"rgba(239,68,68,0.1)":"rgba(255,255,255,0.05)",
                      color:n===""||pinLocked?"transparent":pinError?"#ef4444":"#e2e8f0",
                      borderColor:n===""||pinLocked?"transparent":"rgba(255,255,255,0.1)",
                      opacity:pinLocked?0.3:1,
                      transition:"background .1s"}}>
                      {n}
                    </button>
                  ))}
                </div>

                {pinError && !pinLocked && <div style={{fontSize:12,color:"#ef4444",marginBottom:12,fontWeight:700}}>{_S.pin.wrong}{pinAttempts>=3&&pinAttempts<5?` (${5-pinAttempts} ${_S.pin.attemptsLeft||"محاولات متبقية"})`:""}</div>}

                <div style={{display:"flex",gap:8,justifyContent:"center"}}>
                  <button onClick={()=>{setShowPinModal(false);setPinInput("");setPinError(false);setPendingTab(null);}}
                    style={{padding:"8px 20px",borderRadius:10,background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",color:"#64748b",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif"}}>
                    {_S.pin.cancel}
                  </button>
                  <button onClick={()=>{setPinChanging(true);setPinInput("");setPinError(false);}}
                    style={{padding:"8px 20px",borderRadius:10,background:"rgba(200,160,60,0.1)",border:"1px solid rgba(200,160,60,0.2)",color:"#c8a03c",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif"}}>
                    {_S.pin.change}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{marginBottom:12}}>
                  <label style={{display:"block",fontSize:12,color:"#94a3b8",marginBottom:6,textAlign:"right"}}>{_S.pin.newLabel}</label>
                  <input type="password" value={newPin} onChange={e=>setNewPin(e.target.value)} maxLength={8} placeholder={_S.pin.newPlaceholder}
                    style={{width:"100%",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:10,padding:"10px 14px",color:"#e2e8f0",fontSize:16,textAlign:"center",outline:"none",letterSpacing:4,fontFamily:"monospace",boxSizing:"border-box"}}/>
                </div>
                <div style={{marginBottom:16}}>
                  <label style={{display:"block",fontSize:12,color:"#94a3b8",marginBottom:6,textAlign:"right"}}>{_S.pin.confirmLabel}</label>
                  <input type="password" value={confirmPin} onChange={e=>setConfirmPin(e.target.value)} maxLength={8} placeholder={_S.pin.confirmPlaceholder}
                    style={{width:"100%",background:"rgba(255,255,255,0.05)",border:`1px solid ${pinError?"rgba(239,68,68,0.4)":"rgba(255,255,255,0.1)"}`,borderRadius:10,padding:"10px 14px",color:"#e2e8f0",fontSize:16,textAlign:"center",outline:"none",letterSpacing:4,fontFamily:"monospace",boxSizing:"border-box"}}/>
                </div>
                {pinError && <div style={{fontSize:12,color:"#ef4444",marginBottom:12,fontWeight:700}}>{_S.pin.mismatch}</div>}
                <div style={{display:"flex",gap:8,justifyContent:"center"}}>
                  <button onClick={()=>{setPinChanging(false);setNewPin("");setConfirmPin("");setPinError(false);}}
                    style={{flex:1,padding:"10px",borderRadius:10,background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",color:"#64748b",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif"}}>
                    {_S.pin.back}
                  </button>
                  <button onClick={changePin}
                    style={{flex:1,padding:"10px",borderRadius:10,background:"linear-gradient(135deg,#c8a03c,#9a6f10)",border:"none",color:"#0d0f1a",fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"'Tajawal',sans-serif"}}>
                    {_S.pin.save}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  </LangContext.Provider>
  );
}

// ── Auth Wrapper: Show login if not authenticated ──────────────────
export default function App() {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    // Check current session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUser(session.user);
        fetchUserRole(session.user.id);
      }
      setAuthLoading(false);
    });

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session?.user) {
        setUser(session.user);
        fetchUserRole(session.user.id);
      } else {
        setUser(null);
        setRole(null);
      }
      setAuthLoading(false);
    });

    return () => subscription?.unsubscribe();
  }, []);

  const fetchUserRole = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .single();
      if (error) throw error;
      setRole(data?.role);
    } catch (error) {
      console.error('Error fetching user role:', error);
      setRole(null);
    }
  };

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const logout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setUser(null);
    setRole(null);
  };

  // /sign route: customer signature page — no auth needed
  if (typeof window !== 'undefined' && window.location.pathname === '/sign') {
    return <SignaturePage/>;
  }

  if (authLoading) {
    return (
      <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100vh",background:"#0f172a",fontSize:18,color:"#64748b"}}>
        Loading...
      </div>
    );
  }

  if (!user) {
    return <LoginPage onLogin={login} />;
  }

  return <Dashboard user={user} role={role} onLogout={logout} />;
}
